import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ReactNode } from 'react';
import { Control, useWatch } from 'react-hook-form';

import { ReadOnlyField } from '~components/FormFields/ReadOnlyField';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import NumberInput from '~components/FormFields/v2/NumberInput';

import { RoutingSettingsDto } from './routingSettingsSchema';

interface RouteOptimizationFormProps {
  control: Control<RoutingSettingsDto>;
  disabled: boolean;
  editable: boolean;
}

// Each group is a labelled <section> so the heading owns its controls in the
// accessibility tree, not only visually.
// Card styling mirrors CompanyForm so this reads like General Settings.
const Section = ({
  children,
  description,
  title,
}: {
  children: ReactNode;
  description: string;
  title: string;
}) => (
  <Card
    aria-label={title}
    component="section"
    elevation={0}
    sx={{
      p: 2,
      mb: 2,
      mt: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography component="h2" variant="h6">
      {title}
    </Typography>
    <Typography sx={{ color: 'text.secondary', fontSize: 14, mb: 2, mt: 0.5 }}>
      {description}
    </Typography>
    {children}
  </Card>
);

// Mirrors the info-icon tooltip in OrderMetricsTab. describeChild keeps the tooltip out of
// the field's accessible name.
const LabelWithTooltip = ({ label, tooltip }: { label: string; tooltip?: string }) =>
  tooltip ? (
    <Box component="span" sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.5 }}>
      <span>{label}</span>
      <Tooltip arrow describeChild placement="top" title={tooltip}>
        <InfoOutlined sx={{ color: 'text.secondary', cursor: 'help', fontSize: 14 }} />
      </Tooltip>
    </Box>
  ) : (
    label
  );

// NumberInput's Label is a plain <Typography>, so the input has no accessible
// name. Mirror the label onto the input.
const NumberField = ({
  control,
  disabled,
  editable,
  isRequired,
  label,
  name,
  placeholder,
  tooltip,
}: {
  control: Control<RoutingSettingsDto>;
  disabled: boolean;
  editable: boolean;
  isRequired?: boolean;
  label: string;
  name: keyof RoutingSettingsDto;
  placeholder?: string;
  tooltip?: string;
}) => {
  const value = useWatch({ control, name });

  if (!editable) {
    return (
      <ReadOnlyField
        label={<LabelWithTooltip label={label} tooltip={tooltip} />}
        value={value === '' ? placeholder : String(value)}
      />
    );
  }

  return (
    <NumberInput
      control={control}
      slotProps={{ htmlInput: { 'aria-label': label } }}
      isDisabled={disabled}
      isRequired={isRequired}
      label={<LabelWithTooltip label={label} tooltip={tooltip} />}
      name={name}
      placeholder={placeholder}
    />
  );
};

const BooleanField = ({
  control,
  disabled,
  editable,
  label,
  name,
  tooltip,
}: {
  control: Control<RoutingSettingsDto>;
  disabled: boolean;
  editable: boolean;
  label: string;
  name: keyof RoutingSettingsDto;
  tooltip?: string;
}) => {
  const value = useWatch({ control, name });

  if (!editable) {
    return (
      <ReadOnlyField
        label={<LabelWithTooltip label={label} tooltip={tooltip} />}
        value={value ? t('actions.yes') : t('actions.no')}
      />
    );
  }

  // Bottom-aligned so the checkbox sits level with a NumberInput in the same grid row.
  return (
    <Box sx={{ alignItems: 'flex-end', display: 'flex' }}>
      <CheckboxInput
        control={control}
        isDisabled={disabled}
        label={<LabelWithTooltip label={label} tooltip={tooltip} />}
        name={name}
      />
    </Box>
  );
};

const fieldGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 2,
};

export default function RouteOptimizationForm({
  control,
  disabled,
  editable,
}: RouteOptimizationFormProps) {
  return (
    <Box>
      <Section
        description={t('administration.route_optimization.hours_of_service_description')}
        title={t('administration.route_optimization.hours_of_service')}
      >
        <Box sx={fieldGridSx}>
          <NumberField
            control={control}
            disabled={disabled}
            editable={editable}
            isRequired
            label={t('administration.route_optimization.interstate_drive_hours')}
            name="interstateDriveHours"
          />

          <NumberField
            control={control}
            disabled={disabled}
            editable={editable}
            isRequired
            label={t('administration.route_optimization.interstate_total_hours')}
            name="interstateTotalHours"
          />

          <NumberField
            control={control}
            disabled={disabled}
            editable={editable}
            isRequired
            label={t('administration.route_optimization.intrastate_drive_hours')}
            name="intrastateDriveHours"
          />

          <NumberField
            control={control}
            disabled={disabled}
            editable={editable}
            isRequired
            label={t('administration.route_optimization.intrastate_total_hours')}
            name="intrastateTotalHours"
          />
        </Box>
      </Section>

      <Section
        description={t('administration.route_optimization.plan_coverage_description')}
        title={t('administration.route_optimization.plan_coverage')}
      >
        <Box sx={fieldGridSx}>
          <NumberField
            control={control}
            disabled={disabled}
            editable={editable}
            label={t('administration.route_optimization.min_loads_per_truck')}
            tooltip={t('administration.route_optimization.min_loads_per_truck_tooltip')}
            name="minLoadsPerTruck"
            placeholder={t('administration.route_optimization.no_minimum')}
          />

          <NumberField
            control={control}
            disabled={disabled}
            editable={editable}
            label={t('administration.route_optimization.max_loads_per_truck')}
            name="maxLoadsPerTruck"
            placeholder={t('administration.route_optimization.no_maximum')}
          />

          <NumberField
            control={control}
            disabled={disabled}
            editable={editable}
            label={t('administration.route_optimization.max_orders_per_truck')}
            tooltip={t('administration.route_optimization.max_orders_per_truck_tooltip')}
            name="maxOrdersPerTruck"
            placeholder={t('administration.route_optimization.no_maximum')}
          />

          <BooleanField
            control={control}
            disabled={disabled}
            editable={editable}
            label={t('administration.route_optimization.require_all_trucks')}
            tooltip={t('administration.route_optimization.require_all_trucks_tooltip')}
            name="requireAllTrucks"
          />
        </Box>
      </Section>
    </Box>
  );
}
